// src/component/GetNotification/GetNotification.js

import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { UserContext } from "../../../context/UserContext";
import { FaBell } from "react-icons/fa";

const GetNotification = () => {
  const { user } = useContext(UserContext);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // ✅ USER ID
  const userId =
    user?.userId ||
    user?.id ||
    user?.UserId ||
    user?.customerId;

  console.log("🆔 USER ID =>", userId);

  // ✅ GET API BASE URL FROM ENV
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // ✅ FETCH API
  const fetchNotifications = async () => {
    if (!userId) {
      console.log("❌ USER ID NOT FOUND");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // ✅ API HIT WITH ENV URL
      const response = await axios.get(
        `${API_BASE_URL}Coupan/GetNotificationByUserId`,
        {
          params: {
            userId: userId,
          },
        }
      );

      console.log("✅ FULL API RESPONSE =>", response);
      console.log("✅ API DATA =>", response.data);

      // ✅ ARRAY CHECK
      if (Array.isArray(response.data)) {
        setNotifications(response.data);
      } else {
        setNotifications([]);
      }
    } catch (err) {
      console.log("❌ API ERROR =>", err);

      if (err.response) {
        console.log("❌ ERROR RESPONSE =>", err.response.data);
      }

      setError("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  // ✅ AUTO LOAD
  useEffect(() => {
    fetchNotifications();
  }, [userId]);

  // ✅ FILTERED LIST
  const filteredNotifications = notifications.filter((n) => {
    if (activeTab === "unread") return !n.isRead;
    if (activeTab === "read") return n.isRead;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="p-5 font-sans">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
            <FaBell size={18} className="text-red-600" />
          </div>
          <div>
            <h2 className="m-0 text-lg font-semibold text-gray-900">
              Notifications
            </h2>
            <p className="m-0 text-xs text-gray-500">
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
            </p>
          </div>
        </div>

        {unreadCount > 0 && (
          <span className="bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
            {unreadCount} New
          </span>
        )}
      </div>

      {/* ── TABS ── */}
      <div className="flex gap-1 mb-5 p-1 bg-gray-100 rounded-lg w-fit">
        {["all", "unread", "read"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-all duration-150 border-0 cursor-pointer
              ${activeTab === tab
                ? "bg-white text-red-600 shadow-sm"
                : "bg-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── LOADING SKELETONS ── */}
      {loading && (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 rounded-xl bg-gray-100 animate-pulse"
              style={{ opacity: 1 - (i - 1) * 0.25 }}
            />
          ))}
        </div>
      )}

      {/* ── ERROR ── */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* ── EMPTY ── */}
      {!loading && filteredNotifications.length === 0 && !error && (
        <div className="text-center py-16">
          <FaBell size={44} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm font-medium">
            No notifications found
          </p>
        </div>
      )}

      {/* ── LIST ── */}
      {!loading && (
        <div className="flex flex-col gap-3">
          {filteredNotifications.map((item) => (
            <div
              key={item.id}
              className={`rounded-xl p-4 transition-all duration-200 cursor-pointer
                ${item.isRead
                  ? "bg-white border border-gray-200 hover:border-gray-300"
                  : "bg-red-50 border border-red-200 border-l-4 border-l-red-600 rounded-l-none hover:bg-red-100"
                }`}
            >
              {/* TOP ROW */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0 mt-1
                      ${item.isRead
                        ? "bg-transparent border border-gray-300"
                        : "bg-red-600"
                      }`}
                  />
                  <h4
                    className={`m-0 text-sm font-semibold
                      ${item.isRead ? "text-gray-800" : "text-red-700"}`}
                  >
                    {item.title || "Notification"}
                  </h4>
                </div>

                {!item.isRead && (
                  <span className="bg-red-600 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                    NEW
                  </span>
                )}
              </div>

              {/* BODY */}
              <p
                className={`m-0 mb-3 pl-4 text-xs leading-relaxed
                  ${item.isRead ? "text-gray-500" : "text-red-600"}`}
              >
                {item.body || "No message available"}
              </p>

              {/* DATE */}
              <div
                className={`pl-4 text-xs
                  ${item.isRead ? "text-gray-400" : "text-red-400"}`}
              >
                {item.sentDate
                  ? new Date(item.sentDate).toLocaleString()
                  : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GetNotification;